import { useApolloClient, useMutation } from '@apollo/client';
import { useEventCallback } from '@mui/material/utils';
import { cloneDeep, omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  bufferTime,
  catchError,
  defer,
  firstValueFrom,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { createTransaction, translateInput } from '@work4all/data';
import { evictEntityCache } from '@work4all/data/lib/apollo-extras/evictEntityCache';
import { WaitingQueue } from '@work4all/data/lib/utils/waiting-queue';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { InputErpAnhangAttachementsRelation } from '@work4all/models/lib/Classes/InputErpAnhangAttachementsRelation.entity';
import { InputPromptResult } from '@work4all/models/lib/Classes/InputPromptResult.entity';
import { ModifyShadowServiceContractResult } from '@work4all/models/lib/Classes/ModifyShadowServiceContractResult.entity';
import { ServiceContract } from '@work4all/models/lib/Classes/ServiceContract.entity';
import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ServiceContractDefaultTypeOfBilling } from '@work4all/models/lib/Enums/ServiceContractDefaultTypeOfBilling.enum';

import { useLatest } from '@work4all/utils/lib/hooks/use-latest';
import { AccesorsOf } from '@work4all/utils/lib/paths-of/paths-of';

import {
  ApiErrors,
  ValidationErrors,
} from '../../../../../apollo/ValidationErrors';
import { NEW_ENTITY_ID } from '../../../mask-metadata';
import { IUseShadowObjectApiOptions } from '../../erp/hooks/use-bz-shadow-object-api/IUseShadowObjectApiOptions';
import { PENDING_NONE, PendingChange } from '../../erp/types';
import { OnEditComponent } from '../components/tab-panels/components/components/edit-table/types';

import {
  CREATE_SHADOW_SERVICE_CONTRACT_OBJECT,
  CreateShadowServiceContractObjectResponse,
  CreateShadowServiceContractObjectVars,
  DELETE_SHADOW_SERVICE_CONTRACT_OBJECT,
  DeleteShadowServiceContractObjectResponse,
  DeleteShadowServiceContractObjectVars,
  MODIFY_SHADOW_SERVICE_CONTRACT_ADD_POSITION,
  MODIFY_SHADOW_SERVICE_CONTRACT_MODIFY_POSITION,
  MODIFY_SHADOW_SERVICE_CONTRACT_MOVE_POSITION,
  MODIFY_SHADOW_SERVICE_CONTRACT_OBJECT,
  MODIFY_SHADOW_SERVICE_CONTRACT_REMOVE_POSITION,
  ModifyShadowServiceContractObjectAddComponentResponse,
  ModifyShadowServiceContractObjectAddComponentVars,
  ModifyShadowServiceContractObjectModifyPositionResponse,
  ModifyShadowServiceContractObjectModifyPositionVars,
  ModifyShadowServiceContractObjectMovePositionResponse,
  ModifyShadowServiceContractObjectMovePositionVars,
  ModifyShadowServiceContractObjectRemovePositionResponse,
  ModifyShadowServiceContractObjectRemovePositionVars,
  ModifyShadowServiceContractObjectResponse,
  ModifyShadowServiceContractObjectVars,
  PERSIST_SHADOW_SERVICE_CONTRACT_OBJECT,
  PersistShadowServiceContractObjectResponse,
  PersistShadowServiceContractObjectVars,
} from './use-shadow-service-contract-object-graphql';

export type MutationType = 'create' | 'modify' | 'persist';
export type ValidationErrors = ApiErrors<ServiceContract>;

export interface ShadowObjectApiOptions<T> {
  onComplete?: (data?: T) => void;
}

export type PositionAccessors = AccesorsOf<ServiceContractPosition>;

export interface ShadowServiceContractObjectAddComponentArgs {
  article?: Article | null;
  typeOfPosition?: AddServiceContractPosTypeOfPos;
  defaultTypeOfBill?: ServiceContractDefaultTypeOfBilling;
  articleIds?: number[];
  copyPositions?: number[];
  index?: number;
  defaultText?: string;
  localId?: string;
}

export interface ShadowServiceContractObjectRemovePositionArgs {
  positionIds: number[];
}

export type ShadowServiceContractObjectApiMethods = ReturnType<
  typeof useShadowServiceContractObjectApi
>[1];

interface DefferedWithKey {
  key: string;
  deferred: Observable<ModifyShadowServiceContractResult>;
}
const createDeferredWithKey = (
  key: string,
  fn: () => Promise<unknown>
): DefferedWithKey => {
  return { key, deferred: defer(fn) };
};

export const SERVICE_CONTRACT_NUMBER_CELLS: PositionAccessors[] = [
  'amount',
  'price',
  'price2',
  'servicePricePerYear',
  'percent',
  'discount',
  'discount2',
  'purchasePrice',
  'amountSupplier',
];

export function useShadowServiceContractObjectApi(
  options: IUseShadowObjectApiOptions
) {
  const { entity, id, parentEntity, parentId, contactId, skip } = options;

  const client = useApolloClient();

  const promptResult = useRef<InputPromptResult[]>([]);

  const [errors, setErrors] = useState<ValidationErrors>([]);

  const [shadowServiceContractObject, setShadowServiceContractObject] =
    useState<ModifyShadowServiceContractResult | null>(null);

  const shadowServiceContractRef = useLatest(shadowServiceContractObject);

  const [deferredQueue, setDeferredQueue] = useState<Subject<
    Observable<ModifyShadowServiceContractResult>
  > | null>(() => new Subject());
  const [immediateQueue, setImmediateQueue] =
    useState<Subject<DefferedWithKey> | null>(() => new Subject());
  const [stop, setStop] = useState<Subject<void> | null>(() => new Subject());
  const [stopped, setStopped] = useState<Subject<void> | null>(
    () => new Subject()
  );

  const [isDirty, setIsDirty] = useState(false);
  const [pending, setPending] = useState<PendingChange>(PENDING_NONE);
  const [createLoading, setCreateLoading] = useState(false);

  const init = useCallback(() => {
    setDeferredQueue(new Subject());
    setImmediateQueue(new Subject());
    setStop(new Subject());
    setStopped(new Subject());

    setIsDirty(false);
  }, []);

  const [mutateCreate] = useMutation<
    CreateShadowServiceContractObjectResponse,
    CreateShadowServiceContractObjectVars
  >(CREATE_SHADOW_SERVICE_CONTRACT_OBJECT, {
    onError() {
      setCreateLoading(false);
    },
    onCompleted(response) {
      setCreateLoading(false);
      setShadowServiceContractObject(response.createShadowServiceContract);
      init();
    },
  });

  const [mutateModify] = useMutation<
    ModifyShadowServiceContractObjectResponse,
    ModifyShadowServiceContractObjectVars
  >(MODIFY_SHADOW_SERVICE_CONTRACT_OBJECT);

  const [mutateAddComponent] = useMutation<
    ModifyShadowServiceContractObjectAddComponentResponse,
    ModifyShadowServiceContractObjectAddComponentVars
  >(MODIFY_SHADOW_SERVICE_CONTRACT_ADD_POSITION);

  const [mutateMovePosition] = useMutation<
    ModifyShadowServiceContractObjectMovePositionResponse,
    ModifyShadowServiceContractObjectMovePositionVars
  >(MODIFY_SHADOW_SERVICE_CONTRACT_MOVE_POSITION);

  const [mutateModifyPosition] = useMutation<
    ModifyShadowServiceContractObjectModifyPositionResponse,
    ModifyShadowServiceContractObjectModifyPositionVars
  >(MODIFY_SHADOW_SERVICE_CONTRACT_MODIFY_POSITION);

  const [mutateRemovePosition] = useMutation<
    ModifyShadowServiceContractObjectRemovePositionResponse,
    ModifyShadowServiceContractObjectRemovePositionVars
  >(MODIFY_SHADOW_SERVICE_CONTRACT_REMOVE_POSITION);

  const [mutatePersist] = useMutation<
    PersistShadowServiceContractObjectResponse,
    PersistShadowServiceContractObjectVars
  >(PERSIST_SHADOW_SERVICE_CONTRACT_OBJECT);

  const [mutateDelete] = useMutation<
    DeleteShadowServiceContractObjectResponse,
    DeleteShadowServiceContractObjectVars
  >(DELETE_SHADOW_SERVICE_CONTRACT_OBJECT);

  useEffect(() => {
    if (!deferredQueue || !immediateQueue || !stop) return;
    const sortByPriority = (
      observables: DefferedWithKey[]
    ): DefferedWithKey[] => {
      return observables.sort((a, b) => {
        // Define a simple sorting rule: Function editPosition should be processed before others
        if (a.key === 'editPosition' && b.key !== 'editPosition') return -1;
        if (b.key === 'editPosition' && a.key !== 'editPosition') return 1;
        return 0; // Maintain original order for others
      });
    };
    const subscription = merge(
      deferredQueue.pipe(
        tap(() => setIsDirty(true)),
        takeUntil(stop),
        switchMap((request) => {
          return timer(250).pipe(map(() => request));
        })
      ),
      immediateQueue.pipe(
        tap(() => {
          setIsDirty(true);
        }),
        bufferTime(500),
        map((observables) => sortByPriority(observables)),
        // Flatten the sorted array back into individual Promises (observables)
        mergeMap((sortedObservables) =>
          of(...sortedObservables.map((item) => item.deferred))
        ),
        takeUntil(stop)
      )
    )
      .pipe(
        switchMap((request) => {
          return request.pipe(
            takeUntil(merge(deferredQueue, immediateQueue)),
            catchError(() => {
              // refresh state
              return of(cloneDeep(shadowServiceContractRef.current));
            })
          );
        }),
        tap((result) => {
          setIsDirty(true);
          setShadowServiceContractObject(result);
        })
      )
      .subscribe({
        complete() {
          stopped.next();
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [deferredQueue, immediateQueue, shadowServiceContractRef, stop, stopped]);

  const createShadowServiceContractObject = useEventCallback(() => {
    const variables: CreateShadowServiceContractObjectVars =
      id == null || id === NEW_ENTITY_ID
        ? {
            customerCode: Number(parentId),
            apCode: Number(contactId),
          }
        : {
            serviceContractCode: Number(id),
          };
    if (!skip) {
      setCreateLoading(true);
      mutateCreate({ variables });
    }
  });

  useEffect(createShadowServiceContractObject, [
    mutateCreate,
    entity,
    id,
    parentEntity,
    parentId,
    skip,
    createShadowServiceContractObject,
  ]);

  useEffect(() => {
    const id = shadowServiceContractObject?.id;

    if (id) {
      return () => {
        mutateDelete({ variables: { id } });
      };
    }
  }, [mutateDelete, shadowServiceContractObject?.id]);

  const modify = useEventCallback((value: ServiceContract) => {
    if (!shadowServiceContractObject) return;

    deferredQueue.next(
      defer(() => {
        const transaction = createTransaction({
          name: 'Service Contract Mask - Modify component',
        });
        setPending({
          pending: true,
          type: 'modify',
        });

        return mutateModify({
          variables: {
            id: shadowServiceContractObject.id,
            serviceContract: translateInput(Entities.serviceContract, value),
            promptResult: promptResult.current,
          },
          onCompleted: () => {
            transaction?.finish();
            setErrors([]);
            setPending(PENDING_NONE);
            promptResult.current = [];
          },
          onError: (e) => {
            const parsedErrors = ValidationErrors.parseErrors<ServiceContract>(
              e.graphQLErrors,
              entity
            );
            setErrors(parsedErrors);
            transaction?.finish();
            setPending(PENDING_NONE);
            promptResult.current = [];
          },
        }).then(({ data }) => data.modifyShadowServiceContract);
      })
    );
  });

  const persist = useEventCallback(
    async (attachements?: InputErpAnhangAttachementsRelation) => {
      await waitingQueue.current.waitUntilEmpty();
      if (!shadowServiceContractObject) return;

      let succeeded = true;

      const promise = firstValueFrom(
        stopped.pipe(
          switchMap(async () => {
            const transaction = createTransaction({
              name: 'Service Contract Mask - Persist',
            });
            await mutatePersist({
              variables: {
                id: shadowServiceContractObject.id,
                attachements: attachements,
              },
              onCompleted: () => {
                transaction?.finish();
                evictEntityCache(client, entity);
                setErrors([]);
              },
              onError: (e) => {
                const parsedErrors =
                  ValidationErrors.parseErrors<ServiceContract>(
                    e.graphQLErrors,
                    Entities.serviceContract
                  );
                setErrors(parsedErrors);
                transaction?.finish();
                succeeded = false;
              },
            });
          }),
          tap(() => {
            init();
          })
        )
      );

      stop.next();

      await promise;
      return succeeded;
    }
  );

  const addComponent = useEventCallback(
    async (
      args: ShadowServiceContractObjectAddComponentArgs,
      options?: ShadowObjectApiOptions<ModifyShadowServiceContractObjectAddComponentResponse>
    ) => {
      const {
        article,
        typeOfPosition,
        defaultTypeOfBill,
        localId,
        copyPositions,
        articleIds: incomingArticleIds,
        index,
        defaultText,
      } = args;
      const articleId = article?.id;

      if (!shadowServiceContractObject) return;

      const { id } = shadowServiceContractObject;

      // Use the articleIds from args if provided, otherwise create it from the article id
      const articleIds =
        incomingArticleIds || (articleId ? [articleId] : undefined);

      immediateQueue.next(
        createDeferredWithKey('addPosition', () => {
          const transaction = createTransaction({
            name: 'Service Contract Mask - Add Position',
          });
          setPending({ pending: true, type: 'addPosition' });

          return mutateAddComponent({
            variables: {
              id,
              articleIds,
              typeOfPosition,
              defaultTypeOfBill,
              localId,
              copyPositions,
              index,
              defaultText,
            },
            onCompleted: (data) => {
              transaction?.finish();
              options?.onComplete(data);
              setPending(PENDING_NONE);
            },
            onError: (error) => {
              console.error('addPosition error', error);
              transaction?.finish();
              setPending(PENDING_NONE);
            },
          }).then(({ data }) => data.modifyShadowServiceContractAddPosition);
        })
      );
    }
  );

  const waitingQueue = useRef(new WaitingQueue());

  const editComponent = useEventCallback(
    async (
      result: OnEditComponent<ServiceContractPosition>,
      options?: ShadowObjectApiOptions<ModifyShadowServiceContractObjectModifyPositionResponse>
    ) => {
      // Adding one task in progress so we can wait for it
      await waitingQueue.current.queueSingle();

      try {
        if (!shadowServiceContractObject) {
          waitingQueue.current.releaseSingle();
          return;
        }

        if (
          !shadowServiceContractObject.data.positionList.some(
            (x) => x.id === result.position.id
          )
        ) {
          console.warn('Skip removing removed entity');
          waitingQueue.current.releaseSingle();
          return;
        }
        const { id } = shadowServiceContractObject;
        const { position } = result;

        if (!Object.keys(position).length || !position.id) {
          console.warn(
            'Skipping request. Reason: There is no values or id in request.',
            position
          );
          waitingQueue.current.releaseSingle();
          return;
        }

        const request: Partial<ServiceContractPosition> = {};

        Object.entries(position).forEach((x) => {
          const [field, value] = x;

          if (
            SERVICE_CONTRACT_NUMBER_CELLS.includes(
              field as PositionAccessors
            ) &&
            typeof value === 'string'
          )
            request[field] = parseFloat(value);
          else request[field] = value;
        });

        delete position.__typename;
        const positionToMutate = translateInput<ServiceContractPosition>(
          Entities.serviceContractPosition,
          {
            id: position.id,
            ...request,
          }
        );
        if (Object.keys(omit(positionToMutate, 'code')).length) {
          immediateQueue.next(
            createDeferredWithKey('editPosition', () => {
              const transaction = createTransaction({
                name: 'Service Contract Mask - Modify Component',
              });
              setPending({
                pending: true,
                type: 'modifyPosition',
                positionId: position.id,
                properties: Object.keys(position),
              });
              return mutateModifyPosition({
                variables: {
                  id,
                  position: positionToMutate,
                },
                onCompleted: (data) => {
                  transaction?.finish();
                  options?.onComplete(data);
                  setPending(PENDING_NONE);
                  // Release task
                  waitingQueue.current.releaseSingle();
                },
                onError: () => {
                  // Release task
                  waitingQueue.current.releaseSingle();
                },
              }).then(
                ({ data }) => data.modifyShadowServiceContractModifyPosition
              );
            })
          );
        } else {
          waitingQueue.current.releaseSingle();
        }
      } catch {
        // If there will be error in regualr code then release
        // Much cleaner would be to make finally block, but Subject.next() is not async by design
        waitingQueue.current.releaseSingle();
      }
    }
  );

  const moveComponent = useEventCallback(
    (positionId: number, index: number, _) => {
      if (!shadowServiceContractObject) return;

      const { id } = shadowServiceContractObject;

      deferredQueue.next(
        defer(() => {
          const transaction = createTransaction({
            name: 'Service Contract Mask - Move Component',
          });
          setPending({ pending: true, type: 'movePosition', positionId });
          return mutateMovePosition({
            variables: {
              id,
              positionId,
              index,
            },
            onCompleted: () => {
              transaction?.finish();
              setPending(PENDING_NONE);
            },
            onError: () => {
              transaction?.finish();
              setPending(PENDING_NONE);
            },
          }).then(({ data }) => data.modifyShadowServiceContractMovePosition);
        })
      );
    }
  );

  const removeComponent = useEventCallback(async (positionIds: number[]) => {
    if (!shadowServiceContractObject) return;

    const { id } = shadowServiceContractObject;

    immediateQueue.next(
      createDeferredWithKey('removePosition', () => {
        const transaction = createTransaction({
          name: 'Service Contract Mask - Remove Position',
        });
        setPending({ pending: true, type: 'removePosition' });
        return mutateRemovePosition({
          variables: {
            id,
            positionIds,
          },
          onCompleted: () => {
            transaction?.finish();
            setPending(PENDING_NONE);
          },
          onError: (error) => {
            console.error('removePosition error', error);
            transaction?.finish();
            setPending(PENDING_NONE);
          },
        }).then(({ data }) => data.modifyShadowServiceContractRemovePosition);
      })
    );
  });

  const refetch = createShadowServiceContractObject;

  return [
    shadowServiceContractObject,
    useMemo(
      () => ({
        isDirty,
        pending,
        loading: createLoading,
        promptResult,
        persist,
        modify,
        addComponent,
        editComponent,
        moveComponent,
        removeComponent,
        refetch,
      }),
      [
        isDirty,
        pending,
        createLoading,
        promptResult,
        persist,
        modify,
        addComponent,
        editComponent,
        moveComponent,
        removeComponent,
        refetch,
      ]
    ),
    errors,
  ] as const;
}
